import React from "react";
import { Form, Row, Col, Card, Accordion,Button } from "react-bootstrap";

/**
 * CTIS_DynamicFormRenderer: 
 * A recursive engine that transforms EMA JSON templates into functional React UI.
 */
const CTISDynamicFormRenderer = ({ template, formData, onFieldChange }) => {
  if (!template || !template.items_tree) {
    return <div className="text-muted p-4">No template DNA loaded.</div>;
  }

  // --- RECURSIVE RENDERER ---
  const renderItem = (item) => {
    const isSection = item.role === "section" || item.field_type === "header";

    if (isSection) {
      return (
        <div key={item.id || item.name} className="ctis-section mb-4">
          {/* Section Header */}
          <div className="d-flex align-items-baseline border-bottom mb-3 pb-2">
            <span className="badge bg-secondary me-2" style={{ fontSize: '0.7rem' }}>
              {item.id || "SEC"}
            </span>
            <h5 className="fw-bold mb-0 text-dark">{item.label}</h5>
          </div>
          
          {item.description && (
            <p className="text-muted small mb-3 fst-italic">{item.description}</p>
          )}

          {/* Render Children (The Recursion) */}
          <div className="ps-3 border-start">
            {item.children && item.children.length > 0 ? (
              item.children.map((child) => renderItem(child))
            ) : (
              <span className="text-muted x-small">No sub-fields defined.</span>
            )}
          </div>
        </div>
      );
    }

    // --- FIELD RENDERING ---
    return (
      <Form.Group as={Row} className="mb-3 align-items-center" key={item.id || item.name}>
        <Form.Label column sm={4} className="small fw-bold text-dark">
          {item.label}
          {item.required && <span className="text-danger ms-1">*</span>}
          <div className="text-muted" style={{ fontSize: '0.65rem', fontWeight: '400' }}>
            ID: {item.id}
          </div>
        </Form.Label>
        
        <Col sm={8}>
          {renderInput(item, formData, onFieldChange)}
          {item.description && (
            <Form.Text className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
              {item.description}
            </Form.Text>
          )}
        </Col>
      </Form.Group>
    );
  };

  // --- COMPONENT MAPPING ---
  const renderInput = (item, data, onChange) => {
    const value = data[item.id] || "";
    const isReadOnly = item.read_only || item.validation?.read_only;

    switch (item.component) {
      case "checkbox":
      case "confirmation_box":
        return (
          <Form.Check 
            type="checkbox" 
            checked={!!value} 
            disabled={isReadOnly}
            onChange={(e) => onChange(item.id, e.target.checked)} 
          />
        );

      case "date_picker":
        return (
          <Form.Control 
            type="date" 
            value={value} 
            disabled={isReadOnly}
            onChange={(e) => onChange(item.id, e.target.value)} 
          />
        );

      case "select":
      case "choice":
        return (
          <Form.Select 
            value={value} 
            disabled={isReadOnly}
            onChange={(e) => onChange(item.id, e.target.value)}
          >
            <option value="">-- Select from EMA List --</option>
            {item.validation?.allowed_values?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Form.Select>
        );

      default:
        // Handle "document_upload" or standard text
        if (item.field_type?.toLowerCase().includes("document")) {
          return (
            <div className="p-2 border rounded bg-light d-flex justify-content-between align-items-center">
              <span className="small text-secondary">Ready for upload...</span>
              <Button variant="outline-dark" size="sm" disabled={isReadOnly}>Attach File</Button>
            </div>
          );
        }
        return (
          <Form.Control 
            type="text" 
            value={value} 
            disabled={isReadOnly}
            placeholder={item.field_type} 
            onChange={(e) => onChange(item.id, e.target.value)} 
          />
        );
    }
  };

  return (
    <div className="ctis-dynamic-form">
      {template.items_tree.map((rootNode) => renderItem(rootNode))}
    </div>
  );
};

export default CTISDynamicFormRenderer;